import {
  GROUPING_SIZE_DEFAULT,
  GROUPING_SIZE_MAXIMUM_DEFAULT,
  GROUPING_SIZE_MINIMUM_DEFAULT,
} from '@breakoutlearning/firebase-repository/constants'
import type { InstructorSlideDeckCubit } from '@breakoutlearning/firebase-repository/cubits/InstructorSlideDeckCubit'
import {
  AssignmentGroupingType,
  CompletionMetric,
  type SectionAssignment,
} from '@breakoutlearning/firebase-repository/models/SectionAssignment'
import { AssignmentType } from '@breakoutlearning/firebase-repository/types'
import { dateTime } from '@breakoutlearning/firebase-repository/util'
import { DateTime } from 'luxon'
import { zodResolver } from '@hookform/resolvers/zod'
import { Spinner } from 'components/Spinner'
import { BreakoutSelect } from 'components/design-system/BreakoutSelect'
import { FormLabel } from 'components/design-system/form/FormLabel'
import { PlusIcon } from 'components/icons/Plus'
import { useDialogs } from 'hooks/dialogs'
import { useBreakoutUser } from 'hooks/profile'
import { useRootStore } from 'hooks/rootStore'
import { useLtiJwtRepack } from 'hooks/use-lti-jwt-repack'
import { useLTIMode } from 'hooks/use-lti-mode'
import { useTranslationTyped } from 'i18n/i18n'
import { observer } from 'mobx-react-lite'
import type {
  AssignmentFormValues,
  LtiAssignmentFormValues,
} from 'pages/instructor/slide_deck/AssignmentDialog/schemas'
import {
  getAssignmentSchema,
  getLtiAssignmentSchema,
} from 'pages/instructor/slide_deck/AssignmentDialog/schemas'
import { useCallback, useId, useMemo, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { withoutUndefined } from 'util/object'
import { CreateOrEditSectionDialog } from '../../classes/CreateOrEditSectionDialog'
import { LtiBaseAssignmentDialog } from './LtiBaseAssignmentDialog'
import type { LtiDeepLinkData } from 'components/LtiAutoSubmittedForm'
import { LtiAutoSubmittedForm } from 'components/LtiAutoSubmittedForm'
import { defaultPeerReviewCriteria } from 'pages/instructor/slide_deck/AssignmentDialog/helpers'
import { useFeatureFlags } from 'hooks/featureFlags'
import { ConfigureCustomRubrics } from 'pages/instructor/assignment/dialogs/ConfigureCustomRubrics'
import {
  isRubricEligibleForCustomGrading,
  type CustomRubricsConfig,
} from 'pages/instructor/assignment/dialogs/customRubricsConfig'

export const LtiCreateAssignmentDialog = observer(
  function LtiCreateAssignmentDialog({
    cubit,
  }: {
    cubit: InstructorSlideDeckCubit
  }) {
    const store = useRootStore()
    const { tt } = useTranslationTyped()
    const { ltiModeOn, ltiOptions } = useLTIMode()
    const repack = useLtiJwtRepack()
    const [deepLinkData, setDeepLinkData] = useState<LtiDeepLinkData>()

    // const placement = ltiOptions?.placement

    const scalarsRef =
      useRef<SectionAssignment['data']['assignmentGradingScalars']>(undefined)
    const [completionMetric, setCompletionMetric] = useState<CompletionMetric>(
      CompletionMetric.timeInSession
    )

    const { customRubricsEnabled } = useFeatureFlags()
    const canConfigureCustomRubrics =
      customRubricsEnabled &&
      cubit.rubrics.models.some(isRubricEligibleForCustomGrading)
    const customRubricsRef = useRef<CustomRubricsConfig>({
      enabled: false,
      isValid: true,
      rubricConfigs: [],
    })
    const [customRubricsValid, setCustomRubricsValid] = useState(true)
    const [weightsDialogOpen, setWeightsDialogOpen] = useState(false)

    const hasQuestions = cubit.hasScorableQuizzes
    const hasRubrics = cubit.rubrics.models.length > 0

    const schema = useMemo(() => {
      if (ltiModeOn) return getLtiAssignmentSchema({ tt })

      return getAssignmentSchema({
        tt,
        sectionEndDate: cubit.section.data.sectionEndDate,
        sectionStartDate: cubit.section.data.sectionStartDate,
      })
    }, [
      cubit.section.data.sectionEndDate,
      cubit.section.data.sectionStartDate,
      ltiModeOn,
      tt,
    ])

    const queryParams = store.router.queryParams

    const assignedAtDefaultValue = DateTime.max(
      dateTime(cubit.section.data.sectionStartDate) ?? DateTime.now(),
      DateTime.now()
    )

    const form = useForm<AssignmentFormValues>({
      resolver: zodResolver(schema),
      mode: 'onChange',
      defaultValues: {
        catalogId: queryParams?.catalogId?.toString() || undefined,
        assignmentType: AssignmentType.ManyGroups,
        groupingType: AssignmentGroupingType.manual,
        sectionId: queryParams?.sectionId?.toString() || undefined,
        assignedAt: assignedAtDefaultValue,
        groupingSize: GROUPING_SIZE_DEFAULT,
        groupingSizeMaximum: GROUPING_SIZE_MAXIMUM_DEFAULT,
        groupingSizeMinimum: GROUPING_SIZE_MINIMUM_DEFAULT,
        configureGrading: false,
        forbidStudentGroupCreation: false,
        forbidStudentGroupScheduling: false,
        forbidStudentGroupSelection: false,
        forbidStudentGroupJoining: false,
        forbidStudentGroupLeaving: false,
        peerReviewsEnabled: false,
        peerReviewCriteria: defaultPeerReviewCriteria(tt),
        peerReviewExcludesSelfReview: false,
        peerReviewsHiddenFromReviewees: false,
        showFinalGradeToParticipants: true, // true for new assignments
        gradeAppealsEnabled: false,
      },
    })

    const [{ gradingSumValid, gradingSum }, setGradingSum] = useState<{
      gradingSumValid: boolean
      gradingSum: number
    }>({ gradingSumValid: false, gradingSum: 0 })

    const setGradingSumCallback = useCallback(
      ({ isValid, sumAsInt }: { isValid: boolean; sumAsInt: number }) => {
        setGradingSum({
          gradingSum: sumAsInt,
          gradingSumValid: isValid,
        })
      },
      []
    )

    const submitLTI = useCallback(
      async (data: LtiAssignmentFormValues) => {
        const slideDeckId = cubit.slideDeck.id
        const slideDeckName = cubit.slideDeck.data.slideDeckName
        const catalogId = data.catalogId
        const options = withoutUndefined({
          groupingType: data.groupingType,
          groupingSize: data.groupingSize,
          groupingSizeMinimum: data.groupingSizeMinimum,
          groupingSizeMaximum: data.groupingSizeMaximum,
          forbidStudentGroupCreation: data.forbidStudentGroupCreation,
          forbidStudentGroupScheduling: data.forbidStudentGroupScheduling,
          forbidStudentGroupSelection: data.forbidStudentGroupSelection,
          forbidStudentGroupJoining: data.forbidStudentGroupJoining,
          forbidStudentGroupLeaving: data.forbidStudentGroupLeaving,
          scalars: scalarsRef.current,
          completionMetric,
          organizationId: ltiOptions?.organizationId,
          peerReviewsEnabled: data.peerReviewsEnabled,
          peerReviewCriteria: data.peerReviewCriteria,
          peerReviewExcludesSelfReview: data.peerReviewExcludesSelfReview,
          peerReviewsHiddenFromReviewees: data.peerReviewsHiddenFromReviewees,
          showFinalGradeToParticipants: data.showFinalGradeToParticipants,
          gradeAppealsEnabled: data.gradeAppealsEnabled,
          // Custom rubric config (weights + score mappings). Persisted by the
          // deep-link assignment-creation handler after the assignment exists,
          // since AssignmentRubric docs need the assignmentId. Gate on the live
          // grading toggle too: the panel's ref stays enabled after it unmounts,
          // so this must not leak rubrics onto an ungraded assignment.
          rubricConfigs:
            data.configureGrading && customRubricsRef.current.enabled
              ? customRubricsRef.current.rubricConfigs
              : undefined,
        })

        return repack([
          {
            slideDeckId,
            slideDeckName,
            catalogId,
            options,
          },
        ]).then((result) => {
          setDeepLinkData(result)
        })
      },
      [
        ltiOptions?.organizationId,
        cubit.slideDeck.data.slideDeckName,
        cubit.slideDeck.id,
        repack,
        completionMetric,
      ]
    )

    return (
      <>
        <LtiAutoSubmittedForm deepLinkData={deepLinkData} />
        <LtiBaseAssignmentDialog
          form={form}
          onSubmit={submitLTI}
          releasedToStudents={false}
          hasBeenStarted={false}
          hasQuestions={hasQuestions}
          hasRubrics={hasRubrics}
          submitButtonText={tt.instructor_library.add_to_class()}
          hideDateFields={ltiModeOn}
          ltiMode={ltiModeOn}
          fullScreen={ltiModeOn}
          scalarsRef={scalarsRef}
          completionMetric={completionMetric}
          onCompletionMetricChange={setCompletionMetric}
          sectionSelectComponent={
            ltiModeOn ? null : <SectionSelect cubit={cubit} form={form} />
          }
          gradingSumValidOverride={{
            gradingSum,
            gradingSumValid,
            setGradingSumCallback,
          }}
          slideDeckId={cubit.slideDeckId}
          customRubricsValid={customRubricsValid}
          onCustomizeWeights={() => setWeightsDialogOpen(true)}
          advancedGradingSlot={
            canConfigureCustomRubrics ? (
              <ConfigureCustomRubrics
                rubrics={cubit.sortedRubrics}
                roles={cubit.roles.models}
                configRef={customRubricsRef}
                onValidityChange={setCustomRubricsValid}
                weightsDialogOpen={weightsDialogOpen}
                onWeightsDialogClose={() => setWeightsDialogOpen(false)}
                defaultsManageableInSettings={false}
                seedFromInstructorDefaults
              />
            ) : undefined
          }
        />
      </>
    )
  }
)

const SectionSelect = ({
  cubit,
  form,
}: {
  cubit: InstructorSlideDeckCubit
  form: ReturnType<typeof useForm<AssignmentFormValues>>
}) => {
  const { showDialog } = useDialogs()
  const { tt } = useTranslationTyped()
  const rootStore = useRootStore()
  const user = useBreakoutUser()
  const selectId = useId()

  const mySections = cubit.sections

  if (mySections.isLoading) return <Spinner className="mx-auto" />

  return (
    <Controller
      control={form.control}
      name="sectionId"
      render={({ field, fieldState }) => (
        <BreakoutSelect
          inputClassName="h-[50px]"
          {...field}
          id={selectId}
          testId="section-select"
          error={fieldState.error}
          required
          kind="secondary"
          label={tt.instructor_library.select_class()}
          labelComponent={
            <div className="flex w-full flex-row items-center justify-between">
              <FormLabel
                label={tt.instructor_library.select_class()}
                for={selectId}
                required={true}
              />
              {!user.isTA && (
                <div className="w-full px-4 text-body-medium text-on-surface-var">
                  <button
                    className="w-full whitespace-nowrap text-body-medium text-on-surface-var"
                    data-testid="add-new-section-button"
                    onClick={() => {
                      showDialog(({ remove }) => {
                        return (
                          <CreateOrEditSectionDialog
                            editOrCreate="create"
                            createSectionInvoiceRequest={
                              cubit.createSectionInvoiceRequest
                            }
                            saveSection={cubit.createSection}
                            onSave={({ sectionId }) => {
                              if (!sectionId) return

                              rootStore.updateQueryParams({
                                sectionId,
                              })

                              remove()

                              form.setValue('sectionId', sectionId)
                            }}
                            redeemPromotions={cubit.redeemPromotions}
                            applyApprovedFreeAdminPromotion={
                              cubit.applyApprovedFreeAdminPromotion
                            }
                          />
                        )
                      })
                    }}
                  >
                    <div className="flex items-center justify-end">
                      <PlusIcon
                        size={14}
                        className="mr-0.5 stroke-fixed-accent-color"
                        pathClassName="stroke-fixed-accent-color"
                        pathStrokeWidth={3.5}
                      />
                      {tt.instructor_library.add_new_class()}
                    </div>
                  </button>
                </div>
              )}
            </div>
          }
          name="sectionId"
          placeholder={
            !mySections.value.length
              ? tt.instructor_library.no_classes_found()
              : tt.instructor_library.select_class()
          }
          options={cubit.sortedSectionOptions.value ?? []}
        />
      )}
    />
  )
}
